import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import ConfirmDialog from "../../components/ConfirmDialog";
import useConfirmDialog from "../../hooks/useConfirmDialog";

const OPS_ROLE_OPTIONS = [
  { label: "Support", value: "support" },
  { label: "Super Admin", value: "superadmin" },
];

export default function OpsUsers() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const role = (localStorage.getItem("userRole") || "").toLowerCase();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "support",
    password: "",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const { confirmProps, openConfirm } = useConfirmDialog();
  const fetchOpsUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/ops/users`, { headers });
      setUsers(response.data);
      setError("");
    } catch (err) {
      console.error("OpsUsers fetch error", err.response?.data || err.message);
      setError("Unable to load ops users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpsUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    role: "support",
      password: "",
    });
    setEditingUser(null);
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    try {
      if (editingUser) {
        await axios.put(
          `${API_BASE_URL}/ops/users/${editingUser.user_id}`,
          {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phone,
            role: form.role,
            emailVerified: true,
          },
          { headers }
        );
        setMessage("User updated.");
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/ops/users`,
          {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phone,
            role: form.role,
            password: form.password || undefined,
          },
          { headers }
        );
        const tempPassword = response.data?.tempPassword;
        setMessage(tempPassword ? `Ops user created. Temp password: ${tempPassword}` : "Ops user created.");
      }

      resetForm();
      fetchOpsUsers();
    } catch (err) {
      console.error("OpsUsers submit error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to save user.");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phone: user.phone || "",
      role: user.role,
      password: "",
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_BASE_URL}/ops/users/${userId}`, { headers });
      setMessage("User deleted.");
      fetchOpsUsers();
      if (editingUser?.user_id === userId) {
        resetForm();
      }
    } catch (err) {
      console.error("OpsUsers delete error", err.response?.data || err.message);
      setError("Unable to delete user.");
    }
  };

  const confirmDeleteUser = (user) => {
    openConfirm({
      title: "Delete User",
      message: `Delete ${user.first_name} ${user.last_name}?`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: () => handleDelete(user.user_id),
    });
  };

  return (
    <div className="ops-section">
      <div className="ops-section-header">
        <h3>Ops Users</h3>
      </div>

      {error && <p className="text-danger">{error}</p>}
      {message && <p className="text-success">{message}</p>}

      <div className="d-flex justify-content-end gap-2 mb-3">
        <button
          className="btn btn-primary"
          disabled={role !== "superadmin"}
          onClick={() => {
            if (role !== "superadmin") return;
            setEditingUser(null);
            setForm({
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              role: "support",
              password: "",
            });
            setShowModal(true);
          }}
        >
          Add Ops User
        </button>
      </div>

      <div className="ops-table-wrapper">
        {loading ? (
          <p>Loading users...</p>
        ) : (
          <table className="ops-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id}>
                  <td>{`${user.first_name} ${user.last_name}`}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(user)}>
                      Edit
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => confirmDeleteUser(user)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4}>No ops users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-icon" onClick={resetForm}>
              &times;
            </span>
            <h4>{editingUser ? "Edit Ops User" : "Add Ops User"}</h4>
            <form onSubmit={handleSubmit} className="ops-form mt-3">
              <div className="mb-2">
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Phone</label>
                <input
                  type="text"
                  className="form-control"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Role</label>
                <select className="form-select" name="role" value={form.role} onChange={handleChange}>
                  {OPS_ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {!editingUser && (
                <div className="mb-2">
                  <label className="form-label">Custom Password (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Leave blank to auto-generate"
                  />
                </div>
              )}
              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-primary" type="submit" disabled={loading}>
                  {editingUser ? "Save Changes" : "Create User"}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
